import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { DEFAULT_PROPERTY_TYPES_FILTERS } from "src/components/neighborhoodSearch/constants";
import { userApi } from "../libs/landvoice-api";

import {
  ACREAGE_MIN,
  YEAR_BUILT_MIN,
  ACREAGE_MAX,
  SQFT_MAX,
  SQFT_MIN,
} from "src/constants/neighborhoodSearch";
import { getYearBuiltMax } from "src/utils/neighborhoodSearch";
import { GeoLocation } from "src/types/common";
import { MapTypeId } from "src/components/core/Map";
import { Address } from "src/types/listing";

const SETTINGS_QUERY_KEY = ["neighborhoodSearchSettings"] as const;

export enum LocationType {
  myLocation = "MyLocation",
  address = "Address",
}

export interface NeighborhoodSearchSettings {
  selectedPropertyTypes: string[];
  beds: [number | undefined, number | undefined];
  baths: [number | undefined, number | undefined];
  sqft: [number | undefined, number | undefined];
  acreage: [number | undefined, number | undefined];
  yearBuilt: [number | undefined, number | undefined];
  defaultAddressGeoLocation?: GeoLocation;
  defaultLocationType: LocationType | null;
  defaultAddress?: Address;
  defaultMapTypeId: MapTypeId;
}

export const DEFAULT_SETTINGS: NeighborhoodSearchSettings = {
  selectedPropertyTypes: DEFAULT_PROPERTY_TYPES_FILTERS,
  beds: [undefined, undefined],
  baths: [undefined, undefined],
  sqft: [SQFT_MIN, SQFT_MAX],
  acreage: [ACREAGE_MIN, ACREAGE_MAX],
  yearBuilt: [YEAR_BUILT_MIN, getYearBuiltMax()],
  defaultLocationType: null,
  defaultMapTypeId: MapTypeId.Roadmap,
};

export const useNeighborhoodSearchSettingsQuery = () => {
  return useQuery({
    queryKey: SETTINGS_QUERY_KEY,
    queryFn: async () => {
      const settings = (await userApi.getUserSettings())?.data;

      // Merge API settings with default settings
      const mergedSettings = {
        ...DEFAULT_SETTINGS,
        ...settings,
      };

      // Ensure yearBuilt max is always the current year
      const currentYearMax = getYearBuiltMax();
      if (mergedSettings.yearBuilt?.[1] !== currentYearMax) {
        mergedSettings.yearBuilt = [
          mergedSettings.yearBuilt?.[0] ?? YEAR_BUILT_MIN,
          currentYearMax,
        ];
      }

      return mergedSettings as NeighborhoodSearchSettings;
    },
  });
};

export const useNeighborhoodSearchSettingsMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (settings: NeighborhoodSearchSettings) =>
      userApi.saveUserSettings(settings),
    onSuccess: (_, newSettings) => {
      queryClient.setQueryData(SETTINGS_QUERY_KEY, newSettings);
    },
  });
};

// Helper function to create complete settings object from current state
export const createSettingsObject = (
  currentState: {
    selectedPropertyTypes: string[];
    beds: [number | undefined, number | undefined];
    baths: [number | undefined, number | undefined];
    sqft: [number | undefined, number | undefined];
    acreage: [number | undefined, number | undefined];
    yearBuilt: [number | undefined, number | undefined];
    defaultLocationType: LocationType | null;
    defaultAddress: Address | null;
    defaultMapTypeId: MapTypeId;
    defaultAddressGeoLocation: GeoLocation | null;
  },
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  overrides: Record<string, any> = {}
): NeighborhoodSearchSettings => ({
  selectedPropertyTypes: currentState.selectedPropertyTypes,
  beds: currentState.beds,
  baths: currentState.baths,
  sqft: currentState.sqft,
  acreage: currentState.acreage,
  yearBuilt: currentState.yearBuilt,
  defaultLocationType: currentState.defaultLocationType,
  defaultAddress: currentState.defaultAddress || undefined,
  defaultMapTypeId: currentState.defaultMapTypeId,
  defaultAddressGeoLocation: currentState.defaultAddressGeoLocation || undefined,
  ...overrides,
});
